import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, EmptyState, Panel, Skeleton, Stack, t, Timeline } from '@kanbios/ui';
import type { fleetState } from '@kanbios/api-client';
import { api, unwrap } from '../lib/api';
import { relativeTime, shortDateTime } from '../lib/format';
import { AccessCheckStatus, useAccessCheck } from './AccessCheck';
import { explainAccess } from '../lib/accessHint';
import { ListTable } from './ListTable';

type Synchronisation = fleetState.components['schemas']['Synchronisation'];
type Condition = Synchronisation['conditions'][number];
type Remedy = NonNullable<Synchronisation['diagnosis']['remedy']>;
const STEP_STATUS = { ok: 'done', failed: 'error', unchecked: 'upcoming' } as const;

const STATUS_TONES = { True: 'pos', False: 'neg', Unknown: 'neutral' } as const;
const EVENT_STATUS = { True: 'done', False: 'error', Unknown: 'upcoming' } as const;
const STAGE_TONES: Record<Synchronisation['stage'], 'pos' | 'neg' | 'info' | 'brand'> = {
  enrolled: 'pos', failed: 'neg', enrolling: 'info', to_enrol: 'brand', to_bootstrap: 'brand', not_applicable: 'brand',
};

/**
 * D43-2 (ADR-021), « Synchronisation » tab: what each source last said of the
 * server, how old it is, and the enrolment stage the portal derives from it;
 * then when each of these changed. Nothing here is a status someone wrote.
 */
export function SynchronisationPanel({ server }: { server: string }) {
  const sync = useQuery({
    queryKey: ['server-synchronisation', server],
    queryFn: async () => unwrap(await api.fleetState.GET('/servers/{server}/synchronisation', { params: { path: { server } } })),
    refetchInterval: 30_000,
  });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const check = useAccessCheck();
  // « Relire maintenant »: Checkmk read again, then this tab.
  const refresh = useMutation({
    mutationFn: async () => unwrap(await api.fleetState.POST('/servers/{server}/running/refresh', { params: { path: { server } } })),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['server-synchronisation', server] }),
  });
  const act = (remedy: Remedy) => {
    if (remedy === 'refresh') refresh.mutate();
    else if (remedy === 'check_access') check.run();
    // « Mettre à jour la configuration » goes through the enrolment's review (Accès step, D24); « Amorcer » lives in the Configuration tab.
    else if (remedy === 'update_configuration') navigate(`/servers/import?servers=${encodeURIComponent(server)}`);
    else navigate(`/servers/${encodeURIComponent(server)}?tab=configuration`);
  };
  const s = sync.data;
  if (sync.isPending) return <Skeleton lines={8} />;
  if (!s) return <EmptyState icon="error" title={t('synchronisation.failed')} />;
  const because = s.conditions.find(c => c.type === s.because);

  return (
    <Stack gap={24}>
      <Alert tone={STAGE_TONES[s.stage]} title={t('synchronisation.stage', { stage: t(`servers.enrolment.${s.stage}`) })}>
        {because ? t('synchronisation.because', { condition: because.label }) : t('synchronisation.allTrue')}
      </Alert>
      {s.diagnosis.accessLost && (
        <Alert
          tone="warn"
          title={t('synchronisation.accessLost.title')}
          action={t(`synchronisation.remedy.${s.diagnosis.accessLost.remedy}`)}
          onAction={() => act(s.diagnosis.accessLost!.remedy)}
        >
          {explainAccess(s.diagnosis.accessLost.detail) ?? t('synchronisation.accessLost.body')}
        </Alert>
      )}
      <AccessCheckStatus feedback={check.feedback} />
      {s.diagnosis.failing && (
        <Panel title={t('synchronisation.diagnosis.title')} subtitle={t('synchronisation.diagnosis.subtitle')}>
          <Stack gap={16}>
            <Timeline
              label={t('synchronisation.diagnosis.title')}
              compact
              items={s.diagnosis.steps.map(step => ({
                id: step.id,
                title: t(`synchronisation.diagnosis.steps.${step.id}`),
                description: step.status === 'failed' && step.detail ? step.detail : t(`synchronisation.diagnosis.status.${step.status}`),
                status: STEP_STATUS[step.status],
              }))}
            />
            {s.diagnosis.cause && s.diagnosis.remedy && (
              <Alert
                tone="warn"
                title={t(`synchronisation.diagnosis.causes.${s.diagnosis.cause}`)}
                action={refresh.isPending ? undefined : t(`synchronisation.remedy.${s.diagnosis.remedy}`)}
                onAction={() => act(s.diagnosis.remedy!)}
              >
                {t(`synchronisation.diagnosis.remedies.${s.diagnosis.cause}`)}
              </Alert>
            )}
          </Stack>
        </Panel>
      )}
      <Panel title={t('synchronisation.conditions.title')} subtitle={t('synchronisation.conditions.subtitle')} flush>
        <ListTable<Condition>
          label={t('synchronisation.conditions.title')}
          queryKey={['server-synchronisation-conditions', server]}
          fetch={async query => unwrap(await api.fleetState.GET('/servers/{server}/synchronisation/conditions', { params: { path: { server }, query: query as never } }))}
          rowKey="type"
          defaultSort={{ key: 'type', dir: 'asc' }}
          search={t('synchronisation.search')}
          facets={[
            { key: 'status', label: t('synchronisation.columns.status'), valueLabel: v => t(`synchronisation.status.${v}`) },
            { key: 'source', label: t('synchronisation.columns.source'), valueLabel: v => t(`synchronisation.sources.${v}`) },
          ]}
          columns={[
            { key: 'type', label: t('synchronisation.columns.condition'), width: 220, strong: true, render: (c: Condition) => t(`synchronisation.types.${c.type}`) },
            { key: 'status', label: t('synchronisation.columns.status'), width: 100, render: (c: Condition) => <Badge tone={STATUS_TONES[c.status]}>{t(`synchronisation.status.${c.status}`)}</Badge> },
            // What the source says, then its message, on one line (the full text in the cell's tooltip).
            {
              key: 'label', label: t('synchronisation.columns.detail'), grow: true, width: 260, sortable: false,
              accessor: (c: Condition) => [c.label.split(' : ').slice(1).join(' : '), c.message].filter(Boolean).join(' — '),
            },
            { key: 'source', label: t('synchronisation.columns.source'), width: 130, render: (c: Condition) => t(`synchronisation.sources.${c.source}`) },
            { key: 'observedAt', label: t('synchronisation.columns.age'), width: 120, render: (c: Condition) => relativeTime(c.observedAt) },
            { key: 'since', label: t('synchronisation.columns.since'), width: 150, render: (c: Condition) => shortDateTime(c.since) },
          ]}
          empty={<EmptyState framed={false} icon="sync" title={t('synchronisation.conditions.empty')} />}
        />
      </Panel>
      <Panel title={t('synchronisation.events.title')} subtitle={t('synchronisation.events.subtitle')}>
        {s.events.length ? (
          <Timeline
            label={t('synchronisation.events.title')}
            compact
            items={s.events.map((e, i) => ({
              id: `${e.at}-${e.type}-${i}`,
              title: e.label,
              when: shortDateTime(e.at),
              dateTime: e.at,
              description: t(`synchronisation.sources.${e.source}`),
              status: EVENT_STATUS[e.to],
            }))}
          />
        ) : <EmptyState icon="history" title={t('synchronisation.events.empty')} />}
      </Panel>
    </Stack>
  );
}
